import React from 'react';

import { Modal } from 'react-bootstrap';
import {
  useGetPositionsForIncident,
  useCreateNewPositionForIncident,
  useDeletePositionForIncident,
  useEditPositionForIncident,
} from 'hooks/incident/useRoster';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { useForm } from 'react-hook-form';
import StylishNewSelect from 'components/DesignSystems/New/StylishNewSelect';
import { StylishNewCheckbox } from 'components/DesignSystems/New/StylishNewCheckbox';

type IncidentNewMemberDialogProps = {
  show: boolean;
  onClose: () => void;
};

type ActionOption = {
  value: 'add' | 'edit' | 'delete';
  label: string;
};

type SelectOption = {
  value: string;
  label: string;
};

const ACTION_OPTIONS: ActionOption[] = [
  { value: 'add', label: 'Add Seat' },
  { value: 'edit', label: 'Edit Seat' },
  { value: 'delete', label: 'Delete Seat' },
];

export default function IncidentNewMemberDialog({
  show = true,
  onClose,
}: IncidentNewMemberDialogProps) {
  const {
    clearErrors,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm({ mode: 'onChange' });

  const incidentPositionsQuery = useGetPositionsForIncident();
  const incidentPositions = incidentPositionsQuery.data || [];

  const { mutate: createNew } = useCreateNewPositionForIncident();
  const { mutate: editPosition } = useEditPositionForIncident();
  const { mutate: deletePosition } = useDeletePositionForIncident();

  const [selectedSeat, setSelectedSeat] = React.useState<SelectOption | null>(
    null
  );
  const [selectedManagerSeat, setSelectedManagerSeat] =
    React.useState<SelectOption | null>(null);
  const [seatName, setSeatName] = React.useState<string>('');
  const [selectedAction, setSelectedAction] =
    React.useState<ActionOption | null>(null);

  const [isWorkgroup, setIsWorkgroup] = React.useState<boolean>(false);

  const onManageSeatSubmit = () => {
    switch (selectedAction?.value) {
      case 'add':
        if (!seatName.trim()) {
          setError('seatName', {
            type: 'required',
            message: 'Seat name is required',
          });
          return;
        }
        createNew({
          name: seatName,
          reportsTo: selectedManagerSeat?.value,
          isWorkgroup,
        });
        break;
      case 'edit':
        if (!selectedSeat) {
          setError('seat', {
            type: 'required',
            message: 'Seat selection is required',
          });
        }
        if (!seatName.trim()) {
          setError('seatName', {
            type: 'required',
            message: 'Seat name is required',
          });
        }
        if (errors?.managerSeat?.message || !selectedSeat || !seatName.trim()) {
          return;
        }
        editPosition({
          id: selectedSeat?.value,
          name: seatName,
          reportsTo: selectedManagerSeat?.value,
          isWorkgroup,
        });
        break;
      case 'delete':
        if (!selectedSeat) {
          setError('seat', {
            type: 'required',
            message: 'Seat selection is required',
          });
          return;
        }
        deletePosition({
          id: selectedSeat?.value,
        });
        break;
    }

    onClose();
  };

  const checkForCircularParentage = (
    parentId: any,
    depth: number = 0
  ): boolean => {
    if (depth > 10 || !parentId) return false; // Stop at depth 10 or if no parent
    if (parentId === selectedSeat?.value) return true; // Circular reference found
    const parent = incidentPositions.find(({ id }) => id === parentId);
    return checkForCircularParentage(parent?.parent_id, depth + 1);
  };

  const setManagerSeatBasedOnSelectedSeat = (seatId: string) => {
    const selected = incidentPositions.find(({ id }) => id === seatId);
    if (selected && selected.parent_id) {
      const managerSeat = incidentPositions.find(
        ({ id }) => id === selected?.parent_id
      );
      if (managerSeat) {
        setSelectedManagerSeat({
          value: managerSeat.id,
          label: managerSeat.name,
        });
      } else {
        setSelectedManagerSeat(null);
      }
    } else {
      setSelectedManagerSeat(null);
    }
  };

  const setIsWorkgroupBasedOnSelectedSeat = (seatId: string) => {
    const selected = incidentPositions.find(({ id }) => id === seatId);
    if (selected) {
      setIsWorkgroup(selected.is_workgroup || false);
    } else {
      setIsWorkgroup(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      backdrop="static"
      keyboard={false}
      size="lg"
    >
      <form onSubmit={handleSubmit(onManageSeatSubmit)}>
        <Modal.Header closeButton closeVariant="white">
          <Modal.Title>Manage Incident Seats</Modal.Title>
        </Modal.Header>
        <Modal.Body className="incident-new-member-modal-body">
          <div className="mb-4 incident-seat-actions">
            <StylishNewSelect
              options={ACTION_OPTIONS}
              value={selectedAction}
              onChange={(value) => {
                setSelectedAction(value);
                setSeatName('');
                setSelectedManagerSeat(null);
                setSelectedSeat(null);
                clearErrors('seatName');
                setIsWorkgroup(false);
              }}
              placeholder="Select Action"
              isClearable={true}
              isSearchable={false}
              isMulti={false}
            />
          </div>

          {selectedAction?.value === 'add' && (
            <>
              <div className="mb-4 incident-seat-picker">
                <label className="form-label incident-seat-name-label">
                  New Seat Name
                </label>
                <input
                  type="text"
                  className={`form-control ${errors.seatName ? 'is-invalid' : ''}`}
                  placeholder="Enter new seat name"
                  value={seatName}
                  onChange={(e) => {
                    setSeatName(e.target.value);
                    if (!e.target.value.trim() || errors.seatName) {
                      clearErrors('seatName');
                    }
                  }}
                  onBlur={() => {
                    if (!seatName.trim()) {
                      clearErrors('seatName');
                      setError('seatName', {
                        type: 'required',
                        message: 'Seat name is required',
                      });
                    }
                  }}
                />

                {errors.seatName && (
                  <div className="invalid-feedback">
                    {errors.seatName.message as string}
                  </div>
                )}
              </div>
              <div className="mb-4 incident-seat-picker">
                <label className="form-label incident-seat-name-label">
                  Reports to:
                </label>
                <StylishNewSelect
                  options={incidentPositions.map(({ id, name }) => ({
                    value: id,
                    label: name,
                  }))}
                  value={selectedManagerSeat}
                  onChange={(value) => {
                    setSelectedManagerSeat(value);
                    clearErrors('seat');
                  }}
                  placeholder={'Select a seat'}
                  isClearable={true}
                  isSearchable={true}
                  isMulti={false}
                  isDisabled={false}
                />
              </div>
              <div className="mb-4 incident-seat-picker">
                <StylishNewCheckbox
                  checked={isWorkgroup}
                  onChange={(e) => {
                    setIsWorkgroup(e.currentTarget.checked);
                  }}
                  label="This is a Workgroup"
                />
              </div>
            </>
          )}
          {selectedAction?.value === 'edit' && (
            <>
              <div className="mb-4 incident-seat-picker">
                <label className="form-label incident-seat-picker-label">
                  Select Seat
                </label>
                <StylishNewSelect
                  options={incidentPositions
                    .filter(({ is_template }) => !is_template)
                    .map(({ id, name }) => ({
                      value: id,
                      label: name,
                    }))}
                  value={selectedSeat}
                  onChange={(option) => {
                    setSelectedSeat(option);
                    setSeatName(option.label);
                    clearErrors('seat');
                    clearErrors('seatName');
                    setManagerSeatBasedOnSelectedSeat(option.value);
                    setIsWorkgroupBasedOnSelectedSeat(option.value);
                  }}
                  placeholder={'Select a seat'}
                  isClearable={true}
                  isSearchable={true}
                  isMulti={false}
                  isDisabled={false}
                />
              </div>
              <div className="mb-4 incident-seat-picker">
                <label className="form-label incident-seat-name-label">
                  New Seat Name
                </label>
                <input
                  type="text"
                  className={`form-control ${errors.seatName ? 'is-invalid' : ''}`}
                  placeholder="Enter new seat name"
                  value={seatName}
                  onChange={(e) => {
                    setSeatName(e.target.value);
                    if (!e.target.value.trim() || errors.seatName) {
                      clearErrors('seatName');
                    }
                  }}
                  onBlur={() => {
                    if (!seatName.trim()) {
                      clearErrors('seatName');
                      setError('seatName', {
                        type: 'required',
                        message: 'Seat name is required',
                      });
                    }
                  }}
                />
                {errors.seatName && (
                  <div className="invalid-feedback">
                    {errors.seatName.message as string}
                  </div>
                )}
              </div>
              <div className="mb-4 incident-seat-picker">
                <label className="form-label incident-seat-name-label">
                  Reports to:
                </label>
                <StylishNewSelect
                  options={incidentPositions.map(({ id, name }) => ({
                    value: id,
                    label: name,
                  }))}
                  value={selectedManagerSeat}
                  onChange={(value) => {
                    if (checkForCircularParentage(value?.value)) {
                      setError('managerSeat', {
                        type: 'manual',
                        message:
                          'Circular parentage detected. Please select a different manager.',
                      });
                    } else {
                      clearErrors('managerSeat');
                    }
                    setSelectedManagerSeat(value);
                  }}
                  placeholder={'Select a manager seat'}
                  isClearable={true}
                  isSearchable={true}
                  isMulti={false}
                  isDisabled={false}
                />
                {errors?.managerSeat?.message && (
                  <div style={{ color: 'red' }}>
                    {errors.managerSeat.message as string}
                  </div>
                )}
              </div>
              <div className="mb-4 incident-seat-picker">
                <div className="form-check">
                  <StylishNewCheckbox
                    checked={isWorkgroup}
                    onChange={(e) => {
                      setIsWorkgroup(e.currentTarget.checked);
                    }}
                    label="This is a Workgroup"
                  />
                </div>
              </div>
            </>
          )}
          {selectedAction?.value === 'delete' && (
            <div className="mb-4 incident-seat-picker">
              <label className="form-label incident-seat-picker-label">
                Select Seat
              </label>
              <StylishNewSelect
                options={incidentPositions
                  .filter(({ is_template }) => !is_template)
                  .map(({ id, name: role }) => ({
                    value: id,
                    label: role,
                  }))}
                value={selectedSeat}
                onChange={(value) => {
                  setSelectedSeat(value);
                  clearErrors('seat');
                }}
                placeholder={'Select a seat'}
                isClearable={true}
                isSearchable={true}
                isMulti={false}
                isDisabled={false}
              />
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <div className="incident-dialog-button-group">
            <StylishNewButton
              className="button--secondary button--reverse"
              type="button"
              onClick={onClose}
            >
              Close
            </StylishNewButton>
            <StylishNewButton className="button--primary" type="submit">
              Save Changes
            </StylishNewButton>
          </div>
        </Modal.Footer>
      </form>
    </Modal>
  );
}
